import { DateOnly, DateOnlySchema } from '@/api/common/common.models';

import { getAxiosInstance } from '../api.client';
import {
    type TaskCostAggregate,
    type TaskCostEntry,
    type TaskCostEntryFormModel,
    TaskCostEntrySchema,
} from './taskCostEntries.models';

export async function getTaskCostEntries(
    projectId: string,
    taskId: string,
): Promise<TaskCostEntry[]> {
    const response = await getAxiosInstance().get<TaskCostEntry[]>(
        `/project/${projectId}/task/${taskId}/cost-entry`,
    );
    return response.data;
}

export async function getTaskCostEntryById(
    projectId: string,
    taskId: string,
    id: string,
): Promise<TaskCostEntry> {
    const response = await getAxiosInstance().get<TaskCostEntry>(
        `/project/${projectId}/task/${taskId}/cost-entry/${id}`,
    );

    const parsedData = TaskCostEntrySchema.safeParse(response.data);

    if (!parsedData.success) {
        throw new Error('Invalid cost entry data');
    }

    return parsedData.data;
}

export async function createTaskCostEntry(
    projectId: string,
    taskId: string,
    data: TaskCostEntryFormModel,
): Promise<TaskCostEntry> {
    const response = await getAxiosInstance().postForm<TaskCostEntry>(
        `/project/${projectId}/task/${taskId}/cost-entry`,
        data,
    );
    return response.data;
}

export async function updateTaskCostEntry(
    projectId: string,
    taskId: string,
    id: string,
    data: TaskCostEntryFormModel,
): Promise<TaskCostEntry> {
    const response = await getAxiosInstance().putForm<TaskCostEntry>(
        `/project/${projectId}/task/${taskId}/cost-entry/${id}`,
        data,
    );
    return response.data;
}

export async function deleteTaskCostEntry(
    projectId: string,
    taskId: string,
    id: string,
): Promise<void> {
    await getAxiosInstance().delete(
        `/project/${projectId}/task/${taskId}/cost-entry/${id}`,
    );
}

export async function getCostEntryDates(
    projectId: string,
    startDate: DateOnly,
    endDate: DateOnly,
): Promise<string[]> {
    const isValid =
        DateOnlySchema.safeParse(startDate).success &&
        DateOnlySchema.safeParse(endDate).success;
    if (!isValid) {
        throw new Error('Invalid date range');
    }

    const response = await getAxiosInstance().get<string[]>(
        `/project/${projectId}/cost-entry-dates`,
        {
            params: {
                startDate,
                endDate,
            },
        },
    );
    return response.data;
}

export async function getCostEntriesByDate(
    projectId: string,
    date: DateOnly,
): Promise<TaskCostAggregate[]> {
    const isValid = DateOnlySchema.safeParse(date).success;
    if (!isValid) {
        throw new Error('Invalid date');
    }

    const response = await getAxiosInstance().get<TaskCostAggregate[]>(
        `/project/${projectId}/cost-entries-by-date`,
        {
            params: {
                date,
            },
        },
    );
    return response.data;
}
